import React from 'react';
import Icon from '../../../components/AppIcon';
import { Link } from 'react-router-dom';

const features = [
  {
    headline: 'Build Any Form in Minutes — No Code Needed',
    description:
      'Drag and drop 50+ field types, set up conditional logic, create multi-step flows, and apply per-field styling. Your imagination is the only limit — no developer required.',
    ctaLabel: 'Form Builder',
    ctaTo: '/authentication',
    mockup: (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-7 w-full">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
          <span className="ml-3 text-xs text-gray-400 font-medium">Contact Form</span>
        </div>
        <div className="space-y-3">
          {[
            { label: 'Full Name', active: false, value: 'John Smith' },
            { label: 'Email Address', active: true, value: 'john@company.com' },
            { label: 'Phone Number', active: false, value: '' },
          ]?.map(({ label, active, value }) => (
            <div key={label}>
              <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>
              <div
                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm ${
                  active
                    ? 'border-[#007876] bg-[#007876]/5 shadow-sm'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                {active && <div className="w-1.5 h-1.5 rounded-full bg-[#007876] flex-shrink-0" />}
                <span className={active ? 'text-gray-800 font-medium' : 'text-gray-400 text-xs'}>
                  {value || label}
                </span>
                {active && <Icon name="GripVertical" size={13} className="ml-auto text-gray-300" />}
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 px-3 py-2.5 border-2 border-dashed border-[#007876]/40 rounded-xl text-xs text-[#007876] font-medium">
            <Icon name="Plus" size={13} />
            <span>Drop a field here</span>
          </div>
          <button className="w-full py-2.5 bg-[#007876] text-white text-sm font-semibold rounded-xl mt-1">
            Submit Form
          </button>
        </div>
      </div>
    ),
  },
  {
    headline: 'See Exactly Where Respondents Drop Off',
    description:
      'Real-time conversion funnels, field-level drop-off analysis, device breakdown, and geographic heatmaps. Know exactly where users abandon your forms and fix it fast.',
    ctaLabel: 'Analytics',
    ctaTo: '/authentication',
    mockup: (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-7 w-full">
        <div className="flex items-center justify-between mb-5">
          <span className="text-sm font-bold text-gray-800">Form Performance</span>
          <span className="text-xs text-[#007876] font-semibold flex items-center gap-1">
            <Icon name="TrendingUp" size={12} /> +18% this week
          </span>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { label: 'Views', value: '4,821', up: true },
            { label: 'Starts', value: '2,340', up: true },
            { label: 'Completions', value: '1,876', up: false },
          ]?.map(({ label, value, up }) => (
            <div key={label} className="bg-gray-50 rounded-xl p-3 text-center border border-gray-100">
              <div className="text-base font-bold text-gray-900">{value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{label}</div>
              <div className={`text-xs font-semibold mt-1 ${up ? 'text-[#007876]' : 'text-rose-500'}`}>
                {up ? '↑' : '↓'}
              </div>
            </div>
          ))}
        </div>
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <div className="text-xs text-gray-500 font-medium mb-3">Conversion Rate</div>
          <div className="flex items-end gap-1 h-16">
            {[38, 52, 45, 60, 55, 72, 68, 80, 76, 89]?.map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm"
                style={{
                  height: `${h}%`,
                  background: i >= 8 ? '#007876' : `rgba(0,120,118,${0.12 + i * 0.07})`,
                }}
              />
            ))}
          </div>
        </div>
      </div>
    ),
  },
  {
    headline: 'Collect Payments Inside Your Forms — Zero Friction',
    description:
      'Stripe-powered checkout built right in. Accept one-time payments, subscriptions, and deposits without redirecting users away from your form. Every payment links to its submission.',
    ctaLabel: 'Payments',
    ctaTo: '/authentication',
    mockup: (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-7 w-full">
        <div className="flex items-center justify-between mb-5">
          <span className="text-sm font-bold text-gray-800">Order Summary</span>
          <span className="inline-flex items-center gap-1 text-xs text-[#007876] font-semibold bg-[#007876]/10 px-2 py-1 rounded-full">
            <Icon name="Shield" size={11} /> Secure
          </span>
        </div>
        <div className="space-y-2 mb-5">
          {[
            { item: 'Pro Plan (Annual)', price: '$120.00' },
            { item: 'Setup Fee', price: '$29.00' },
          ]?.map(({ item, price }) => (
            <div key={item} className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{item}</span>
              <span className="font-semibold text-gray-800">{price}</span>
            </div>
          ))}
          <div className="border-t border-gray-100 pt-2 flex items-center justify-between">
            <span className="text-sm font-bold text-gray-900">Total</span>
            <span className="text-lg font-bold text-gray-900">$149.00</span>
          </div>
        </div>
        <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-xs text-gray-500 mb-3">
          <Icon name="CreditCard" size={13} className="text-gray-400" />
          <span>•••• •••• •••• 4242</span>
          <span className="ml-auto text-gray-400">Visa</span>
        </div>
        <button className="w-full py-2.5 bg-[#007876] text-white text-sm font-semibold rounded-xl flex items-center justify-center gap-2">
          <Icon name="Lock" size={14} />
          Pay $149.00 Securely
        </button>
      </div>
    ),
  },
  {
    headline: 'Your Brand. Your Clients. Zero Trace of Us.',
    description:
      "Custom domain, your logo, your colors, your email sender name. Resell WebForms.biz to clients under your own brand — they'll never know we exist. Full white-label portals included.",
    ctaLabel: 'White Label',
    ctaTo: '/authentication',
    mockup: (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-7 w-full">
        <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-100">
          <div className="w-9 h-9 rounded-xl bg-[#007876] flex items-center justify-center">
            <Icon name="Globe" size={18} className="text-white" />
          </div>
          <div>
            <div className="text-sm font-bold text-gray-900">YourBrand.com</div>
            <div className="text-xs text-gray-400">Custom domain active</div>
          </div>
          <span className="ml-auto text-xs text-[#007876] font-semibold bg-[#007876]/10 px-2 py-1 rounded-full">Live</span>
        </div>
        <div className="space-y-2.5">
          {[
            { icon: 'Globe', text: 'forms.yourbrand.com', label: 'Custom Domain' },
            { icon: 'Palette', text: 'Your brand colors applied', label: 'Brand Colors' },
            { icon: 'Mail', text: 'noreply@yourbrand.com', label: 'Email Sender' },
            { icon: 'Users', text: '12 client portals active', label: 'Client Portals' },
          ]?.map(({ icon, text, label }) => (
            <div key={label} className="flex items-center gap-3 px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-100">
              <div className="w-7 h-7 bg-[#007876]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                <Icon name={icon} size={14} className="text-[#007876]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold text-gray-700">{label}</div>
                <div className="text-xs text-gray-400 truncate">{text}</div>
              </div>
              <Icon name="CheckCircle" size={14} className="text-[#007876] flex-shrink-0" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    headline: 'Connect Your Forms Without Writing a Line of Code',
    description:
      'Native Zapier integration, custom webhooks, and a full REST API. Route form submissions to your CRM, Slack, spreadsheets, or any tool in your stack — automatically.',
    ctaLabel: 'Integrations',
    ctaTo: '/authentication',
    mockup: (
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-7 w-full">
        <div className="flex items-center justify-between mb-5">
          <span className="text-sm font-bold text-gray-800">Connected Apps</span>
          <span className="text-xs text-gray-400">6 active</span>
        </div>
        <div className="grid grid-cols-3 gap-2.5 mb-4">
          {[
            { name: 'Zapier', icon: 'Zap', color: 'text-orange-500', bg: 'bg-orange-50 border-orange-100' },
            { name: 'Stripe', icon: 'CreditCard', color: 'text-blue-500', bg: 'bg-blue-50 border-blue-100' },
            { name: 'Slack', icon: 'MessageSquare', color: 'text-purple-500', bg: 'bg-purple-50 border-purple-100' },
            { name: 'HubSpot', icon: 'BarChart2', color: 'text-orange-400', bg: 'bg-orange-50 border-orange-100' },
            { name: 'REST API', icon: 'Code2', color: 'text-[#007876]', bg: 'bg-[#007876]/10 border-[#007876]/20' },
            { name: 'Webhooks', icon: 'Globe', color: 'text-green-600', bg: 'bg-green-50 border-green-100' },
          ]?.map(({ name, icon, color, bg }) => (
            <div key={name} className={`flex flex-col items-center gap-1.5 p-3 ${bg} rounded-xl border`}>
              <Icon name={icon} size={18} className={color} />
              <span className="text-xs text-gray-600 font-medium text-center">{name}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 px-3 py-2.5 bg-[#007876]/5 rounded-xl border border-[#007876]/20">
          <Icon name="Plus" size={14} className="text-[#007876]" />
          <span className="text-xs text-[#007876] font-semibold">Connect more tools through Zapier</span>
        </div>
      </div>
    ),
  },
];

const DetailedFeaturesGrid = () => {
  return (
    <section style={{ backgroundColor: '#eeeef8' }} className="py-16 sm:py-20 lg:py-28">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        {features?.map((feature, i) => {
          const isEven = i % 2 === 0;
          return (
            <div
              key={i}
              className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-10 lg:gap-16 py-16 lg:py-20 ${
                i < features?.length - 1 ? 'border-b border-white/50' : ''
              }`}
            >
              {/* Mockup side — large white card */}
              <div className="w-full lg:w-1/2 flex justify-center">
                <div className="w-full max-w-[520px]">
                  {feature?.mockup}
                </div>
              </div>
              {/* Text side */}
              <div className="w-full lg:w-1/2 flex flex-col justify-center">
                <h3
                  className="font-extrabold text-gray-900 leading-tight mb-5"
                  style={{ fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', letterSpacing: '-0.025em' }}
                >
                  {feature?.headline}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-8" style={{ fontSize: '1.0625rem' }}>
                  {feature?.description}
                </p>
                <Link
                  to={feature?.ctaTo}
                  className="inline-flex items-center gap-1.5 font-bold text-base group w-fit"
                  style={{ color: '#007876' }}
                >
                  <span>Go to {feature?.ctaLabel}</span>
                  <span className="text-lg group-hover:translate-x-1 transition-transform duration-200 inline-block">→</span>
                </Link>
              </div>
            </div>
          );
        })}

        {/* Bottom CTA strip */}
        <div className="mt-8 rounded-2xl bg-gradient-to-r from-[#007876] to-[#005f5d] p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-1">Ready to see it in action?</h3>
            <p className="text-white/70 text-sm">Start on the free plan and build your first form.</p>
          </div>
          <Link
            to="/authentication"
            className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 bg-white text-[#007876] font-semibold text-sm rounded-xl hover:bg-gray-50 transition-colors shadow-sm w-full sm:w-auto justify-center"
          >
            <span>Start Free</span>
            <Icon name="ArrowRight" size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default DetailedFeaturesGrid;